import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { hrRequisitionApi } from "../api";
import { assetApi } from "#/features/assets/api";
import { inventoryApi } from "#/features/inventory/api";
import type { HrRequisitionLine, ProcurementItemType } from "../types";

function useAsyncData<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(String(e?.message ?? e)); setLoading(false); } });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, rev]);

  return { data, loading, error, reload: () => setRev((r) => r + 1) };
}

// Existing (already persisted) lines only support lineOrder/description/quantity edits —
// PUT /{id}/lines/{lineId} has no classification fields (confirmed 2026-07-08). To change a
// line's itemType/category, delete it here and add a fresh one instead.
type ExistingLine = { id: number; lineOrder: number; description: string; quantity: number };
type NewLine = Omit<HrRequisitionLine, "id" | "approvedQuantity" | "issuedQuantity" | "fulfillmentStatus">;

function emptyNewLine(order: number, itemType?: ProcurementItemType | null): NewLine {
  return { lineOrder: order, description: "", quantity: 1, itemType: itemType ?? null, inventoryItemId: null, assetCategoryId: null };
}

export default function RequisitionEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [purpose, setPurpose] = useState("");
  const [existingLines, setExistingLines] = useState<ExistingLine[] | null>(null);
  const [deletedLineIds, setDeletedLineIds] = useState<number[]>([]);
  const [newLines, setNewLines] = useState<NewLine[]>([]);

  const { data: req, loading, error } = useAsyncData(
    () => hrRequisitionApi.get(Number(id)).then((r) => {
      setPurpose(r.purpose ?? "");
      setExistingLines((r.lines ?? []).filter((l): l is HrRequisitionLine & { id: number } => l.id != null).map((l) => ({
        id: l.id, lineOrder: l.lineOrder, description: l.description, quantity: l.quantity,
      })));
      setDeletedLineIds([]);
      setNewLines([]);
      return r;
    }),
    [id],
  );

  const reqType = req?.requisitionType ?? req?.type;
  const isGoods = reqType === "GOODS";
  const isAsset = reqType === "ASSET_REQUEST";
  const newLineItemType: ProcurementItemType | null =
    isGoods ? "CONSUMABLE" : isAsset ? "CAPITAL_ASSET" : reqType === "SERVICE" ? "SERVICE" : reqType === "WORKS" ? "WORKS" : null;

  const { data: inventoryItems } = useAsyncData(
    () => isGoods ? inventoryApi.listItems({ size: 200 }).then(r => r.content) : Promise.resolve([]),
    [isGoods],
  );
  const { data: assetCategories } = useAsyncData(
    () => isAsset ? assetApi.listCategories() : Promise.resolve([]),
    [isAsset],
  );

  function updateExistingLine(i: number, field: keyof ExistingLine, value: string | number) {
    setExistingLines((prev) => prev ? prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l) : prev);
  }

  function removeExistingLine(i: number) {
    setExistingLines((prev) => {
      if (!prev) return prev;
      const removed = prev[i];
      setDeletedLineIds((ids) => [...ids, removed.id]);
      return prev.filter((_, idx) => idx !== i);
    });
  }

  function addNewLine() {
    setNewLines((prev) => [...prev, emptyNewLine((existingLines?.length ?? 0) + prev.length + 1, newLineItemType)]);
  }

  function updateNewLine(i: number, field: keyof NewLine, value: unknown) {
    setNewLines((prev) => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  }

  function removeNewLine(i: number) {
    setNewLines((prev) => prev.filter((_, idx) => idx !== i));
  }

  function pickInventoryItem(lineIdx: number, itemId: number) {
    const item = (inventoryItems ?? []).find(it => it.id === itemId);
    updateNewLine(lineIdx, "inventoryItemId", itemId);
    if (item) updateNewLine(lineIdx, "description", item.name);
  }

  function pickAssetCategory(lineIdx: number, catId: number) {
    const cat = (assetCategories ?? []).find(c => c.id === catId);
    updateNewLine(lineIdx, "assetCategoryId", catId);
    if (cat) updateNewLine(lineIdx, "description", `${cat.name} (${cat.code})`);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!existingLines) return;
    if (existingLines.some((l) => !l.description.trim()) || newLines.some((l) => !l.description.trim())) {
      setSaveError("All lines must have a description.");
      return;
    }
    // Backend rejects (400) new GOODS/CONSUMABLE lines with no linked inventoryItemId.
    if (isGoods && newLines.some((l) => !l.inventoryItemId)) {
      setSaveError("Every new Goods line must be linked to a catalog inventory item — pick one from the dropdown.");
      return;
    }
    // Not backend-enforced yet (same class of gap, flagged 2026-07-13), but a category-less
    // Asset Request line is equally unfulfillable — guard client-side regardless.
    if (isAsset && newLines.some((l) => !l.assetCategoryId)) {
      setSaveError("Every new Asset Request line must be linked to an asset category — pick one from the dropdown.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const reqId = Number(id);
      if (purpose !== (req?.purpose ?? "")) {
        await hrRequisitionApi.update(reqId, { purpose });
      }
      await Promise.all(deletedLineIds.map((lineId) => hrRequisitionApi.deleteLine(reqId, lineId)));
      await Promise.all(existingLines.map((l) =>
        hrRequisitionApi.updateLine(reqId, l.id, { lineOrder: l.lineOrder, description: l.description, quantity: l.quantity }),
      ));
      await Promise.all(newLines.map((l) => hrRequisitionApi.addLine(reqId, l)));
      navigate(`/hr/requisitions/${id}`);
    } catch (err: unknown) {
      setSaveError(String((err as { message?: string })?.message ?? err));
      setSaving(false);
    }
  }

  if (loading) return <div className="rounded-xl border bg-white p-5 text-sm text-slate-500">Loading…</div>;
  if (error) return <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>;
  if (!req || !existingLines) return null;

  if (req.status !== "DRAFT") {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Only <strong>DRAFT</strong> requisitions can be edited.{" "}
        <Link to={`/hr/requisitions/${id}`} className="font-semibold text-emerald-700 hover:underline">
          View requisition
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to={`/hr/requisitions/${id}`} className="text-sm text-emerald-700 hover:underline">
          &larr; Back to Requisition
        </Link>
      </div>

      <div className="rounded-2xl border-2 border-emerald-700 bg-white p-5 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Staff Requisition — Edit</p>
        <h1 className="mt-0.5 text-xl font-bold text-slate-900">{req.serialNo || `Requisition #${req.id}`}</h1>
      </div>

      {saveError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{saveError}</div>
      )}

      <form onSubmit={handleSave} className="space-y-5">
        <div className="rounded-2xl border bg-white p-5 shadow-sm space-y-3">
          <h2 className="border-b pb-2 font-bold text-slate-900">Purpose</h2>
          <textarea
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            rows={2}
            placeholder="Briefly describe the need for this requisition…"
            className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        <div className="rounded-2xl border bg-white p-5 shadow-sm space-y-4">
          <h2 className="border-b pb-2 font-bold text-slate-900">Existing Lines</h2>
          <p className="text-xs text-slate-400 -mt-2">
            Description and quantity can be changed here. To change item type or category, remove the line and add a new one below.
          </p>

          {existingLines.length === 0 && (
            <p className="text-sm text-slate-400">No existing lines — add new ones below.</p>
          )}

          {existingLines.map((line, i) => (
            <div key={line.id} className="grid gap-3 sm:grid-cols-4 items-end border-b pb-4 last:border-0 last:pb-0">
              <div className="sm:col-span-3">
                <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">Description *</label>
                <input
                  type="text"
                  value={line.description}
                  onChange={(e) => updateExistingLine(i, "description", e.target.value)}
                  required
                  className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">Qty</label>
                  <input
                    type="number"
                    min={1}
                    value={line.quantity}
                    onChange={(e) => updateExistingLine(i, "quantity", Number(e.target.value))}
                    className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeExistingLine(i)}
                  className="rounded-xl border border-red-200 px-3 py-2 text-xs text-red-500 hover:bg-red-50"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border bg-white p-5 shadow-sm space-y-4">
          <h2 className="border-b pb-2 font-bold text-slate-900">Add New Lines</h2>

          {newLines.map((line, i) => (
            <div key={i} className="grid gap-3 sm:grid-cols-4 items-end border-b pb-4 last:border-0 last:pb-0">
              {isGoods && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">
                    Inventory Item <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={line.inventoryItemId ?? ""}
                    onChange={(e) => pickInventoryItem(i, Number(e.target.value))}
                    required
                    className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">— select item —</option>
                    {(inventoryItems ?? []).map(it => (
                      <option key={it.id} value={it.id}>{it.name} ({it.issueUnitName})</option>
                    ))}
                  </select>
                </div>
              )}
              {isAsset && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">
                    Asset Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={line.assetCategoryId ?? ""}
                    onChange={(e) => pickAssetCategory(i, Number(e.target.value))}
                    required
                    className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">— select category —</option>
                    {(assetCategories ?? []).filter(c => c.assignable !== false).map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">Description *</label>
                <input
                  type="text"
                  value={line.description}
                  onChange={(e) => updateNewLine(i, "description", e.target.value)}
                  required
                  readOnly={isGoods || isAsset}
                  className={`w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${isGoods || isAsset ? "bg-slate-50 text-slate-500" : ""}`}
                  placeholder={isGoods ? "Select an inventory item above" : isAsset ? "Select an asset category above" : undefined}
                />
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">Qty</label>
                  <input
                    type="number"
                    min={1}
                    value={line.quantity}
                    onChange={(e) => updateNewLine(i, "quantity", Number(e.target.value))}
                    className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeNewLine(i)}
                  className="rounded-xl border border-red-200 px-3 py-2 text-xs text-red-500 hover:bg-red-50"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addNewLine}
            className="rounded-xl border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
          >
            + Add Line
          </button>
        </div>

        <div className="flex justify-end gap-3">
          <Link
            to={`/hr/requisitions/${id}`}
            className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-emerald-700 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
